/**
 * AssetLibraryPanel - the studio's kept asset libraries (logos /
 * signatures / pictures) as a panel ANY editor can mount.
 *
 * The libraries are the user's asset collection, stored once in
 * this browser under the shared keys in utils/assetLibrary
 * (`oc-asset-library:v1:*`). The Check Studio and the Document
 * Editor read and write the SAME stores, so a signature saved in
 * one appears in the other - this panel is the Document Editor's
 * window onto them (parity pass, 2026-11-05).
 *
 * Rules inherited from the Check Studio build: uploads are KEPT,
 * never silently replaced; a duplicate upload points at the kept
 * copy; a full library refuses with a visible note and evicts
 * nothing; placing is always an explicit tile tap; each entry
 * deletes on its own. Placed copies belong to the design - the
 * panel only reports taps through onPlace. A host can also hand
 * the panel an externally produced asset (a Lab-drawn signature)
 * through the imperative addExternal handle, so it joins the same
 * kept library.
 */

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
    AssetEntry, AssetKind, ASSET_LIBRARY_CAPS,
    loadAssetLibrary, saveAssetLibrary, makeAssetEntry, addAssetEntry, removeAssetEntry
} from '../utils/assetLibrary';
import { downscaleImageFile } from '../utils/imageIngest';

export interface AssetLibraryPanelHandle {
    /** Keep an externally produced asset (e.g. a Lab-drawn or
     * typed signature). Returns the kept entry, or the existing
     * entry when the image was already kept. */
    addExternal: (kind: AssetKind, name: string, src: string) => AssetEntry;
    /** The current libraries (read access for the host editor). */
    getLibraries: () => Record<AssetKind, AssetEntry[]>;
}

export interface AssetLibraryPanelColors {
    text: string;
    textSecondary: string;
    border: string;
    card: string;
    bg: string;
    primary: string;
}

interface AssetLibraryPanelProps {
    colors: AssetLibraryPanelColors;
    /** Asset ids currently placed on the document (marked on tiles). */
    placedAssetIds?: Set<string>;
    /** A tile was tapped: place this entry on the document. */
    onPlace: (kind: AssetKind, entry: AssetEntry) => void;
    /** Called after any library mutation (not on mount), so the
     * host editor can react if it needs to. */
    onLibrariesChange?: (libs: Record<AssetKind, AssetEntry[]>) => void;
}

const KIND_META: Record<AssetKind, { label: string; icon: string; placeVerb: string; noun: string }> = {
    logos: { label: 'Logos', icon: '🏷️', placeVerb: 'Place logo', noun: 'logo' },
    signatures: { label: 'Signatures', icon: '✒️', placeVerb: 'Use signature', noun: 'signature' },
    pictures: { label: 'Pictures & stamps', icon: '🖼️', placeVerb: 'Place picture', noun: 'picture' }
};

const KIND_ORDER: AssetKind[] = ['logos', 'signatures', 'pictures'];

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error || new Error('Could not read the file'));
        r.readAsDataURL(file);
    });
}

export const AssetLibraryPanel = forwardRef<AssetLibraryPanelHandle, AssetLibraryPanelProps>(
    function AssetLibraryPanel({ colors, placedAssetIds, onPlace, onLibrariesChange }, ref) {
        const [libraries, setLibraries] = useState<Record<AssetKind, AssetEntry[]>>(() => ({
            logos: loadAssetLibrary('logos'),
            signatures: loadAssetLibrary('signatures'),
            pictures: loadAssetLibrary('pictures')
        }));
        const [notes, setNotes] = useState<Partial<Record<AssetKind, string>>>({});
        const fileRefs = useRef<Partial<Record<AssetKind, HTMLInputElement | null>>>({});
        const librariesRef = useRef(libraries);
        librariesRef.current = libraries;
        const firstRender = useRef(true);

        // Persist every mutation to the shared stores (the Check
        // Studio's exact write path), so the other editor sees it.
        useEffect(() => {
            (Object.keys(libraries) as AssetKind[]).forEach(kind => { saveAssetLibrary(kind, libraries[kind]); });
            if (firstRender.current) { firstRender.current = false; return; }
            onLibrariesChange?.(libraries);
        }, [libraries]);

        useImperativeHandle(ref, () => ({
            addExternal: (kind, name, src) => {
                const res = addAssetEntry(librariesRef.current[kind], makeAssetEntry(name, src), ASSET_LIBRARY_CAPS[kind]);
                if (res.added) {
                    setLibraries(libs => ({ ...libs, [kind]: res.entries }));
                    return res.entry;
                }
                if (!res.added && res.reason === 'duplicate') return res.existing;
                // Cap: the asset still gets used by the caller (it
                // lives in the design); it just is not kept. Say so.
                setNotes(n => ({ ...n, [kind]: `Your ${KIND_META[kind].label.toLowerCase()} library is full (${ASSET_LIBRARY_CAPS[kind]}), so this one was used but not kept.` }));
                return makeAssetEntry(name, src);
            },
            getLibraries: () => librariesRef.current
        }), []);

        const handleUpload = async (kind: AssetKind, e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            try {
                // Signatures are kept raw (the ink derivation needs
                // the original strokes); logos/pictures shrink on
                // ingest so the shared store stays lean.
                const src = kind === 'signatures' ? await readFileAsDataUrl(file) : await downscaleImageFile(file);
                const res = addAssetEntry(librariesRef.current[kind], makeAssetEntry(file.name || 'Upload', src), ASSET_LIBRARY_CAPS[kind]);
                if (!res.added && res.reason === 'cap') {
                    setNotes(n => ({ ...n, [kind]: `Your ${KIND_META[kind].label.toLowerCase()} library is full (${ASSET_LIBRARY_CAPS[kind]}). Delete one below to add another.` }));
                    return;
                }
                if (!res.added && res.reason === 'duplicate') {
                    setNotes(n => ({ ...n, [kind]: `"${res.existing.name}" is already in your library - tap it below to place it.` }));
                    return;
                }
                if (res.added) {
                    setLibraries(libs => ({ ...libs, [kind]: res.entries }));
                    setNotes(n => ({ ...n, [kind]: `"${res.entry.name}" saved to your library. Tap it below to place it.` }));
                }
            } catch {
                setNotes(n => ({ ...n, [kind]: 'That file could not be read - nothing was added.' }));
            }
        };

        const handleDelete = (kind: AssetKind, id: string) => {
            setLibraries(libs => ({ ...libs, [kind]: removeAssetEntry(libs[kind], id) }));
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <p style={{ margin: 0, color: colors.textSecondary, fontSize: '12px' }}>
                    Your kept logos, signatures and pictures - the same libraries the Check Studio uses.
                    Uploads are kept, never replaced; tap a tile to place it on this document.
                </p>
                {KIND_ORDER.map(kind => {
                    const meta = KIND_META[kind];
                    const entries = libraries[kind];
                    return (
                        <div key={kind} style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <span style={{ color: colors.text, fontSize: '12px', fontWeight: '600' }}>{meta.icon} {meta.label} · {entries.length}/{ASSET_LIBRARY_CAPS[kind]}</span>
                                <button
                                    type="button"
                                    onClick={() => fileRefs.current[kind]?.click()}
                                    style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, cursor: 'pointer', fontSize: '11px' }}
                                >
                                    📤 Add
                                </button>
                                <input
                                    ref={el => { fileRefs.current[kind] = el; }}
                                    type="file"
                                    accept="image/*"
                                    aria-label={`Upload ${meta.label}`}
                                    style={{ display: 'none' }}
                                    onChange={e => handleUpload(kind, e)}
                                />
                            </div>
                            {notes[kind] && (
                                <div style={{ marginBottom: '6px', padding: '6px 8px', borderRadius: '6px', background: `${colors.border}40`, color: colors.text, fontSize: '11px' }}>{notes[kind]}</div>
                            )}
                            {entries.length === 0 ? (
                                <p style={{ margin: 0, color: colors.textSecondary, fontSize: '11px' }}>Nothing kept yet.</p>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {entries.map(entry => {
                                        const placed = !!placedAssetIds?.has(entry.id);
                                        return (
                                            <div key={entry.id} style={{ width: '76px' }}>
                                                <button
                                                    type="button"
                                                    aria-label={`${meta.placeVerb} ${entry.name}`}
                                                    onClick={() => onPlace(kind, entry)}
                                                    title={entry.name}
                                                    style={{
                                                        width: '76px', height: '52px', padding: '3px', borderRadius: '8px', cursor: 'pointer',
                                                        border: placed ? `2px solid ${colors.primary}` : `1px solid ${colors.border}`,
                                                        background: '#FFF', position: 'relative', overflow: 'hidden'
                                                    }}
                                                >
                                                    <img src={entry.src} alt={entry.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: 'auto' }} />
                                                    {placed && <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, background: colors.primary, color: '#FFF', fontSize: '8px', fontWeight: '700', padding: '1px 0' }}>ON DOCUMENT</span>}
                                                </button>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px' }}>
                                                    <span style={{ fontSize: '9px', color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '52px' }}>{entry.name}</span>
                                                    <button
                                                        type="button"
                                                        aria-label={`Delete ${meta.noun} ${entry.name}`}
                                                        onClick={() => handleDelete(kind, entry.id)}
                                                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#EF4444', fontSize: '11px', padding: '0 2px' }}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
);

export default AssetLibraryPanel;
